import React from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import { WorkspaceResponse } from "../../api/workspaces";
import { EvidenceItemResponse } from "../../api/workspaceAi";
import styles from "./EvidenceCompilationPanel.module.css";

interface Props {
  workspace: WorkspaceResponse;
  evidenceItems: EvidenceItemResponse[];
}

/**
 * Placeholder panel for the Evidence Compilation tab.
 *
 * Future work (DEV-420 through DEV-425) will build out the full compilation
 * pipeline: text extraction, PII screening, token budget management, and
 * the two delivery modes (copy/paste prompt & direct API).
 */
const EvidenceCompilationPanel: React.FC<Props> = ({
  workspace,
  evidenceItems,
}) => {
  const { t } = useAppTranslation("pages");

  const hasEvidence = evidenceItems.length > 0;

  /* Rough token estimate: ~4 chars per token for English text */
  const estimatedTokens = evidenceItems.reduce((sum, item) => {
    const textLength = (item.title?.length ?? 0) + (item.description?.length ?? 0) + (item.collectionNotes?.length ?? 0);
    return sum + Math.ceil(textLength / 4);
  }, 0);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h3 className={styles.title}>{t("workspace.compilation.title")}</h3>
        <p className={styles.description}>
          {t("workspace.compilation.description")}
        </p>
      </div>

      {!hasEvidence ? (
        <div className={styles.emptyState}>
          <p>{t("workspace.compilation.noEvidence")}</p>
        </div>
      ) : (
        <div className={styles.summaryCards}>
          <div className={styles.card}>
            <span className={styles.cardLabel}>
              {t("workspace.compilation.totalItems")}
            </span>
            <span className={styles.cardValue}>{evidenceItems.length}</span>
          </div>
          <div className={styles.card}>
            <span className={styles.cardLabel}>
              {t("workspace.compilation.totalTokens")}
            </span>
            <span className={styles.cardValue}>
              ~{estimatedTokens.toLocaleString()}
            </span>
          </div>
          <div className={styles.card}>
            <span className={styles.cardLabel}>
              {t("workspace.compilation.piiStatus")}
            </span>
            <span className={styles.cardValue}>
              {t("workspace.compilation.notScreened")}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default EvidenceCompilationPanel;
